import { clsx } from 'clsx'
import { type FC } from 'react'
import { FormattedMessage } from 'react-intl'

import { PrescriptionPrescriptionTypeEnum } from '@redteclab/api/clients/bully'

import { productAccessorGetLowestPrice } from '../../../../model/productAccessor/productAccessorGetLowestPrice'
import { ProductFormattedUnitPrice } from '../../../ProductFormattedUnitPrice'
import { useProductPageContext } from '../../../productPageContext'
import { ProductPriceOriginal } from '../../../ProductPrice/ProductPriceOriginal'
import { useProductPrices } from '../../hooks/useProductPrices'
import { ProductDiscountBadge } from '../../ProductDiscountBadge'
import { RetailPrice } from '../../ProductVariantHeader/RetailPrice'

const additionalChargePrescriptions = new Set<PrescriptionPrescriptionTypeEnum>(
  [
    PrescriptionPrescriptionTypeEnum.FreePrescription,
    PrescriptionPrescriptionTypeEnum.InsurancePrescription,
  ],
)

const NoLongerVariant = (
  <div className="border-b border-b-dark-primary-min py-5 text-xl text-dark-primary-strong">
    <FormattedMessage id="productVariants.label.noLongerVariant" />
  </div>
)

const OutOfStock = (
  <div className="border-b border-b-dark-primary-min py-5 text-xl text-dark-primary-strong">
    <FormattedMessage id="general.label.outOfStock" />
  </div>
)

export const ProductBuyBoxStickyPriceInfo: FC = () => {
  const { currentPrescriptionType, isMasterPage, offerId, product } =
    useProductPageContext()
  const {
    discount,
    displayPrice,
    formattedUnitPrice,
    isDiscountValid,
    isProductNotInCatalog,
    isProductOutOfSale,
    isProductOutOfStock,
    showThirtyDaysPrice,
  } = useProductPrices({ currentPrescriptionType, offerId, product })
  if (isProductNotInCatalog || isProductOutOfSale) {
    return NoLongerVariant
  }

  if (isProductOutOfStock && !isMasterPage) {
    return OutOfStock
  }

  return (
    <div className="float-right mr-5 flex flex-col border-b-0 border-b-dark-primary-min pb-2">
      <div className="flex flex-col items-start">
        {additionalChargePrescriptions.has(currentPrescriptionType) && (
          <div>
            <FormattedMessage id="general.label.additionalCharge" />
            {': '}
          </div>
        )}
        <div
          className={clsx(
            'mb-1.5 flex items-center',
            currentPrescriptionType ===
              PrescriptionPrescriptionTypeEnum.PrivatePrescription
              ? 'flex-row-reverse gap-2'
              : 'flex-row gap-1',
          )}
        >
          {!isMasterPage && (
            <ProductDiscountBadge
              currentPrescriptionType={currentPrescriptionType}
              data-qa-id="product-discount-badge"
              discount={discount}
              isDiscountValid={isDiscountValid}
              offerPrice={displayPrice}
              showInfo
              showThirtyDaysPrice={showThirtyDaysPrice}
            />
          )}
          {(displayPrice ?? isMasterPage) ? (
            <RetailPrice
              currentPrescriptionType={currentPrescriptionType}
              data-qa-id="product-price"
              isDiscountValid={isDiscountValid}
              isMasterPage={isMasterPage}
              lowestPrice={productAccessorGetLowestPrice(product.variants)}
              offerPrice={displayPrice}
            />
          ) : null}
        </div>
      </div>
      {formattedUnitPrice && !isMasterPage ? (
        <ProductFormattedUnitPrice
          classNames="text-s"
          formattedUnitPrice={formattedUnitPrice}
        />
      ) : null}
      <ProductPriceOriginal
        className="text-xs text-dark-primary-strong"
        currentPrescriptionType={currentPrescriptionType}
        offerId={offerId}
        product={product}
      />
    </div>
  )
}
